import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Stepper from '../components/Stepper';
import EcfBadge from '../components/EcfBadge';
import PruebasDatosPanel from '../components/PruebasDatosPanel';
import AprobacionesPanel from '../components/AprobacionesPanel';
import SimulacionPanel from '../components/SimulacionPanel';
import RepresentacionImpresaPanel from '../components/RepresentacionImpresaPanel';
import { ACTOR_UI, PORTAL_DGII_URL, STEP_DEFS } from '../catalogos';
import { ecfService, type EcfConfig, type InboundLogRow } from '../../../../services/ecf.service';

/**
 * Pestaña "Certificación": asistente de los 15 pasos del portal de certificación de la DGII.
 *
 * El proceso mezcla cosas que se hacen aquí, cosas que se hacen en el portal de la DGII, y
 * esperas a que ellos revisen. Sin distinguirlas la empresa se queda bloqueada sin saber si le
 * toca actuar. Por eso cada paso declara su `actor` y el panel cambia de color y de texto según
 * quién tiene la pelota.
 *
 * El progreso se guarda en `certification_step` de la configuración.
 */

interface CertificacionTabProps {
  config: EcfConfig;
  onChange: (patch: Partial<EcfConfig>) => void;
}

const KIND_LABEL: Record<string, string> = {
  semilla: 'Semilla',
  token: 'Token',
  recepcion: 'Recepción',
  aprobacion: 'Aprobación',
};

export default function CertificacionTab({ config, onChange }: CertificacionTabProps) {
  const currentStep = config.certification_step;
  const [selected, setSelected] = useState(currentStep);
  const [guardando, setGuardando] = useState(false);

  const [log, setLog] = useState<InboundLogRow[]>([]);
  const [logError, setLogError] = useState<string | null>(null);
  const [cargandoLog, setCargandoLog] = useState(false);

  const step = STEP_DEFS.find((s) => s.id === selected) ?? STEP_DEFS[0];
  const actor = ACTOR_UI[step.actor];
  const doneCount = currentStep - 1;
  const progreso = Math.round((doneCount / STEP_DEFS.length) * 100);
  const esPasoActual = step.id === currentStep;
  const muestraTraza = [8, 9, 10, 11].includes(step.id);

  /**
   * La traza de los webservices entrantes es la ÚNICA visibilidad de los pasos 8 a 11: la DGII
   * llama cuando quiere y no informa de nada, así que sin esta tabla no hay forma de saber si el
   * sistema respondió. Se carga solo al abrir esos pasos, para no consultar en los otros doce.
   */
  const cargarLog = useCallback(async () => {
    setCargandoLog(true);
    setLogError(null);
    try {
      setLog(await ecfService.inboundLog(50));
    } catch (err) {
      setLogError(err instanceof Error ? err.message : 'No se pudo cargar la traza');
    } finally {
      setCargandoLog(false);
    }
  }, []);

  useEffect(() => {
    if (muestraTraza) cargarLog();
  }, [muestraTraza, cargarLog]);

  /**
   * El progreso se guarda en el servidor, no solo en memoria: la certificación dura días y se
   * retoma desde otro navegador o después de cerrar sesión. Se toma como buena la configuración
   * que devuelve el backend, para que la pantalla no se separe nunca de lo que hay guardado.
   */
  const irAPaso = async (destino: number, mensaje: string) => {
    setGuardando(true);
    try {
      const actualizado = await ecfService.updateConfig({ certificationStep: destino });
      onChange(actualizado);
      setSelected(destino);
      toast.success(mensaje);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar el progreso');
    } finally {
      setGuardando(false);
    }
  };

  const avanzar = () =>
    irAPaso(Math.min(currentStep + 1, STEP_DEFS.length), `Paso ${currentStep} completado`);

  /** Deshace una marca puesta por error: ahora que el avance se guarda, recargar ya no la borra. */
  const reabrir = (id: number) => irAPaso(id, `Paso ${id} marcado como pendiente`);

  return (
    <div className="space-y-6">
      {/* Progreso */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Certificación ante la DGII</h3>
            <p className="text-sm text-gray-600">
              Paso {currentStep} de {STEP_DEFS.length} · {doneCount} completados
            </p>
          </div>
          <a
            href={PORTAL_DGII_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-900 whitespace-nowrap"
          >
            Abrir portal DGII
            <i className="ri-external-link-line ml-1"></i>
          </a>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all"
            style={{ width: `${progreso}%` }}
          ></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 items-start">
        {/* Índice de pasos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <Stepper steps={STEP_DEFS} currentStep={currentStep} onSelect={setSelected} />

          <div className="mt-6 pt-4 border-t border-gray-200 space-y-1.5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Quién actúa
            </p>
            {(Object.keys(ACTOR_UI) as Array<keyof typeof ACTOR_UI>).map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${ACTOR_UI[k].dot}`}></span>
                <span className="text-xs text-gray-600">{ACTOR_UI[k].titulo}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Detalle del paso */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">
                  {step.phase} · Paso {step.id}
                </p>
                <h3 className="text-lg font-semibold text-gray-900 mt-0.5">{step.label}</h3>
              </div>
              {step.id < currentStep ? (
                <EcfBadge variant="success">Completado</EcfBadge>
              ) : step.id === currentStep ? (
                <EcfBadge variant="info">En curso</EcfBadge>
              ) : (
                <EcfBadge variant="muted">Pendiente</EcfBadge>
              )}
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-700">{step.desc}</p>

            {/* Bloque de actor */}
            <div className={`border rounded-lg p-4 ${actor.box}`}>
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <i className={actor.icon}></i>
                {actor.titulo}
              </p>

              {step.actor === 'portal' && (
                <p className="text-sm text-gray-700 mt-2">
                  Entra al{' '}
                  <a
                    href={PORTAL_DGII_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline"
                  >
                    portal de certificación
                  </a>{' '}
                  y completa este paso allí. Cuando lo hayas hecho, márcalo aquí para seguir.
                </p>
              )}

              {step.actor === 'dgii' && (
                <p className="text-sm text-gray-700 mt-2">
                  No hay nada que hacer de tu lado. La DGII revisa y responde en el portal; suele
                  tardar entre uno y varios días hábiles.
                </p>
              )}

              {step.actor === 'auto' && (
                <p className="text-sm text-gray-700 mt-2">
                  Tus webservices responden solos a lo que la DGII envíe. Abajo está la traza de lo
                  que ha llegado, para confirmar que están recibiendo.
                </p>
              )}
            </div>

            {/* Paso 7 y 12: enlace a la pestaña de servicios */}
            {(step.id === 7 || step.id === 12) && (
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900 mb-1">URLs a registrar</p>
                <p className="text-xs text-gray-500 mb-3">
                  Las tres URLs y el valor exacto que se teclea en el portal están en la pestaña de
                  servicios web, con botón para copiarlas.
                </p>
                <Link
                  to="/taxes/ecf/configuracion?tab=servicios"
                  className="text-sm text-blue-600 hover:text-blue-900"
                >
                  Ir a Servicios web
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            )}

            {/* Paso 2: set de pruebas de la DGII, ya cableado al backend */}
            {step.id === 2 && <PruebasDatosPanel config={config} />}

            {/* Paso 3: set de aprobaciones comerciales */}
            {step.id === 3 && <AprobacionesPanel config={config} />}

            {/* Paso 4: mismo set, con los datos reales de la empresa */}
            {step.id === 4 && <SimulacionPanel config={config} />}

            {/* Paso 5: representaciones impresas de los comprobantes del paso 4 */}
            {step.id === 5 && <RepresentacionImpresaPanel emisorRnc={config.emisor_rnc} />}

            {/* Pasos 8 a 11: traza de lo que la DGII nos envió */}
            {muestraTraza && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">
                    Traza de los webservices entrantes
                  </p>
                  <button
                    onClick={cargarLog}
                    disabled={cargandoLog}
                    className="text-sm text-blue-600 hover:text-blue-900 whitespace-nowrap disabled:opacity-50"
                  >
                    <i
                      className={`${cargandoLog ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} mr-1`}
                    ></i>
                    Actualizar
                  </button>
                </div>

                {logError && (
                  <div className="px-4 py-3 bg-red-50 text-red-700 text-sm border-b border-red-200">
                    <i className="ri-error-warning-line mr-2"></i>
                    {logError}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hora
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          e-NCF
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Resultado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {log.map((l) => (
                        <tr key={l.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                            {new Date(l.created_at).toLocaleString('es-DO', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {KIND_LABEL[l.kind] ?? l.kind}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-gray-700">
                            {l.encf ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <EcfBadge variant={l.ok ? 'success' : 'danger'}>
                              {l.ok ? 'OK' : `Error ${l.http_status}`}
                            </EcfBadge>
                            <span className="block text-xs text-gray-500 mt-1">{l.detail}</span>
                          </td>
                        </tr>
                      ))}

                      {!cargandoLog && !log.length && (
                        <tr>
                          <td colSpan={4} className="px-4 py-4 text-center text-sm text-gray-500">
                            Todavía no ha llegado ninguna llamada. Comprueba que las URLs
                            registradas en el portal apuntan a este servidor.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Avance */}
            {esPasoActual && step.id < STEP_DEFS.length && (
              <div className="flex justify-end pt-2">
                <button
                  onClick={avanzar}
                  disabled={guardando}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {guardando ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      {actor.cta}
                      <i className="ri-arrow-right-line ml-2"></i>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Paso ya completado: se puede reabrir si se marcó por error. */}
            {step.id < currentStep && (
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => reabrir(step.id)}
                  disabled={guardando}
                  className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-go-back-line mr-1"></i>
                  Marcar como pendiente
                </button>
              </div>
            )}

            {!esPasoActual && step.id > currentStep && (
              <p className="text-xs text-gray-500 pt-2">
                Este paso todavía no está disponible. Completa primero el paso {currentStep}.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
